import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '../types'
import { api } from '../services/api'

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  
  // Actions
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string) => Promise<void>
  logout: () => void
  refreshAccessToken: () => Promise<void>
  updateUser: (user: Partial<User>) => void
  checkAuth: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,

      login: async (email: string, password: string) => {
        set({ isLoading: true })
        try {
          const response = await api.post('/auth/login', { email, password })
          if (response.data.success) {
            const { user, accessToken, refreshToken } = response.data.data
            set({
              user,
              accessToken,
              refreshToken,
              isAuthenticated: true,
              isLoading: false,
            })
            // Set token in API client
            api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
          }
        } catch (error: any) {
          set({ isLoading: false })
          throw new Error(error.response?.data?.error || 'Login failed')
        }
      },

      register: async (email: string, password: string) => {
        set({ isLoading: true })
        try {
          const response = await api.post('/auth/register', { email, password })
          if (response.data.success) {
            const { user, accessToken, refreshToken } = response.data.data
            set({
              user,
              accessToken,
              refreshToken,
              isAuthenticated: true,
              isLoading: false,
            })
            api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
          }
        } catch (error: any) {
          set({ isLoading: false })
          throw new Error(error.response?.data?.error || 'Registration failed')
        }
      },

      logout: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        })
        delete api.defaults.headers.common['Authorization']
        // Clear all storage
        localStorage.removeItem('halcode-auth')
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get()
        if (!refreshToken) {
          get().logout()
          return
        }
        try {
          const response = await api.post('/auth/refresh', { refreshToken })
          if (response.data.success) {
            const { accessToken, refreshToken: newRefreshToken } = response.data.data
            set({
              accessToken,
              refreshToken: newRefreshToken,
            })
            api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
          }
        } catch (error) {
          get().logout()
        }
      },

      updateUser: (userData: Partial<User>) => {
        const { user } = get()
        if (user) {
          set({ user: { ...user, ...userData } })
        }
      },

      checkAuth: async () => {
        const { accessToken, refreshToken } = get()
        
        if (!accessToken && !refreshToken) {
          set({ isLoading: false, isAuthenticated: false })
          return
        }
        
        if (accessToken) {
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
        }
        
        try {
          const response = await api.get('/auth/me')
          if (response.data.success) {
            set({
              user: response.data.data,
              isAuthenticated: true,
              isLoading: false,
            })
          } else {
            // Try refresh token
            if (refreshToken) {
              await get().refreshAccessToken()
              const retryResponse = await api.get('/auth/me')
              if (retryResponse.data.success) {
                set({
                  user: retryResponse.data.data,
                  isAuthenticated: true,
                  isLoading: false,
                })
              } else {
                get().logout()
              }
            } else {
              get().logout()
            }
          }
        } catch (error) {
          set({ isLoading: false, isAuthenticated: false })
        }
      },
    }),
    {
      name: 'halcode-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
    }
  )
)

// Initialize auth check on app start
export const initAuth = async () => {
  const { checkAuth } = useAuthStore.getState()
  await checkAuth()
}
